import { EMessengerChatSourceType, EMessengerEventBusEvents, EMessengerScreenName } from '@xometry/ui';
import { amplitudeLogger } from 'amplitude';
import { ReactComponent as BankIcon } from 'assets/images/icons/bank.min.svg';
import { ReactComponent as BarChartIcon } from 'assets/images/icons/bar-chart.min.svg';
import { ReactComponent as BoxIcon } from 'assets/images/icons/box.min.svg';
import { ReactComponent as CalendarIcon } from 'assets/images/icons/calendar.min.svg';
import { ReactComponent as ContactsIcon } from 'assets/images/icons/contacts.min.svg';
import { ReactComponent as EuroIcon } from 'assets/images/icons/euro-outlined.min.svg';
import { ReactComponent as ExpressIcon } from 'assets/images/icons/express.min.svg';
import { ReactComponent as ExternalLinkIcon } from 'assets/images/icons/external-link.min.svg';
import { ReactComponent as FileSyncIcon } from 'assets/images/icons/file-sync.min.svg';
import { ReactComponent as InvoiceIcon } from 'assets/images/icons/invoice-outlined.min.svg';
import { ReactComponent as MessengerIcon } from 'assets/images/icons/messenger-no-messages.min.svg';
import { ReactComponent as SalesforceIcon } from 'assets/images/icons/salesforce.min.svg';
import { ReactComponent as UserIcon } from 'assets/images/icons/user.min.svg';
import { AmplitudeAttributeKey, AmplitudeMessengerEvent, MessengerEntryPoint } from 'collections/amplitudeEvents';
import { convertNumberToGshCode } from 'helpers/gsh/convertNumberToGshCode';
import { IProvider } from 'interfaces/graphql';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { routes } from 'routes';
import { gshLinksPagePath } from 'routes/gsh';
import { useStore } from 'stores/RootStore';
import { ESideOverlayType, IMessengerInitialScreenParams } from 'stores/SideOverlay/SideOverlayStore';
import { DeviceTypes, useDeviceType } from 'utils/hooks/useDeviceType';
import { useEmbedded } from 'utils/hooks/useEmbedded';
import { useMessengerEventBus } from 'utils/hooks/useMessengerEventBus';
import { withMessengerReinitLayoutMode } from 'utils/messenger/withMessengerReinitLayoutMode';

import { SidebarInner, SidebarMenu, SidebarMenuItem, SidebarMenuItemLink, SidebarTitle } from './Shared.styled';

const REPORT_ID = '00O7Q0000075p0UUAQ';

interface Props {
  provider: IProvider;
}

const SidebarFC = ({ provider }: Props) => {
  const { salesforceId, id: partnerId, name: partnerName, billingAddress } = provider;

  const { sideOverlayStore, erpMessengerLayoutStore } = useStore();
  const { context: sideOverlayContext, openSideOverlay, patchMessengerOverlay } = sideOverlayStore;

  const { sendEvent } = useMessengerEventBus();
  const { isEmbedded } = useEmbedded();
  const deviceType = useDeviceType();

  const isMobile = deviceType === DeviceTypes.MOBILE && !isEmbedded;

  const handleChatClick: React.MouseEventHandler<HTMLAnchorElement> = () => {
    const messengerContext: IMessengerInitialScreenParams = {
      initialScreen: {
        name: EMessengerScreenName.CHAT,
        params: {
          chatSource: {
            id: String(partnerId),
            title: `${partnerName} — ${billingAddress?.country}`,
            sourceType: EMessengerChatSourceType.PARTNER,
            sourceId: String(partnerId),
          },
        },
      },
    };

    if (sideOverlayContext.type === ESideOverlayType.Messenger) {
      patchMessengerOverlay({
        ...messengerContext,
        messengerLayoutPresentation: 'forceClassic',
      });

      sendEvent?.(
        EMessengerEventBusEvents.REINITIALIZE_MC,
        withMessengerReinitLayoutMode({
          userPreference: erpMessengerLayoutStore.userErpMessengerLayoutMode,
          isMobile,
          isEmbedded,
          overlayContext: sideOverlayStore.context,
          payload: messengerContext,
        }),
      );
    } else {
      openSideOverlay({
        type: ESideOverlayType.Messenger,
        messengerLayoutPresentation: 'forceClassic',
        ...messengerContext,
      });
    }

    amplitudeLogger({
      event: AmplitudeMessengerEvent.MESSENGER_OPENED,
      attrs: {
        [AmplitudeAttributeKey.ENTRY_POINT]: MessengerEntryPoint.SIDEBAR,
        sourceType: EMessengerChatSourceType.PARTNER,
        sourceId: String(partnerId),
        mode: erpMessengerLayoutStore.userErpMessengerLayoutMode,
      },
    });
  };

  return (
    <SidebarInner>
      <SidebarMenu>
        <SidebarMenuItemLink
          to={`${routes.providerPath(partnerId)}/edit`}
          selected={window.location.pathname.indexOf('edit') >= 0}
        >
          <SidebarTitle className="p-l-xs">
            <UserIcon />
            Profile
          </SidebarTitle>
        </SidebarMenuItemLink>
        <SidebarMenuItemLink
          to={`${routes.providerPath(partnerId)}/contacts`}
          selected={window.location.pathname.indexOf('contacts') >= 0}
        >
          <SidebarTitle className="p-l-xs">
            <ContactsIcon />
            Contacts
          </SidebarTitle>
        </SidebarMenuItemLink>

        <SidebarMenuItemLink
          to={routes.providerBalancePath(partnerId)}
          selected={window.location.pathname.indexOf('balance') >= 0}
        >
          <SidebarTitle className="p-l-xs">
            <BankIcon />
            Finance
          </SidebarTitle>
        </SidebarMenuItemLink>

        <SidebarMenuItem
          href={gshLinksPagePath(convertNumberToGshCode(`PR-${provider.id}`))}
          target="_blank"
          rel="noopener noreferrer"
        >
          <SidebarTitle className="p-l-xs">
            <UserIcon />
            Partner Page (GSH)
            <ExternalLinkIcon />
          </SidebarTitle>
        </SidebarMenuItem>

        {salesforceId != null && (
          <SidebarMenuItem
            href={routes.external.salesforceReportById(REPORT_ID, salesforceId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <SidebarTitle className="p-l-xs">
              <SalesforceIcon />
              SF Cases <ExternalLinkIcon />
            </SidebarTitle>
          </SidebarMenuItem>
        )}

        <SidebarMenuItem
          href={`${routes.providerPath(partnerId)}/production_orders`}
          selected={window.location.pathname.indexOf('production_orders') >= 0}
        >
          <SidebarTitle className="p-l-xs">
            <BoxIcon />
            Production Orders
          </SidebarTitle>
        </SidebarMenuItem>
        <SidebarMenuItem
          href={`${routes.providerPath(partnerId)}/invoices`}
          selected={window.location.pathname.indexOf('invoices') >= 0}
        >
          <SidebarTitle className="p-l-xs">
            <InvoiceIcon />
            Invoices
          </SidebarTitle>
        </SidebarMenuItem>
        <SidebarMenuItem
          href={`${routes.providerPath(partnerId)}/job_offers`}
          selected={window.location.pathname.indexOf('job_offers') >= 0}
        >
          <SidebarTitle className="p-l-xs">
            <FileSyncIcon />
            Job Offers (ERP)
          </SidebarTitle>
        </SidebarMenuItem>
        <SidebarMenuItem
          href={`${routes.providerPath(partnerId)}/rfq_offers`}
          selected={window.location.pathname.indexOf('rfq_offers') >= 0}
        >
          <SidebarTitle className="p-l-xs">
            <EuroIcon />
            RFQ Offers
          </SidebarTitle>
        </SidebarMenuItem>
        <SidebarMenuItem
          href={`${routes.providerPath(partnerId)}/shippings`}
          selected={window.location.pathname.indexOf('shippings') >= 0}
        >
          <SidebarTitle className="p-l-xs">
            <ExpressIcon />
            Shippings
          </SidebarTitle>
        </SidebarMenuItem>
        <SidebarMenuItem
          href={`${routes.providerPath(partnerId)}/rating_by_parts`}
          selected={window.location.pathname.indexOf('rating_by_parts') >= 0}
        >
          <SidebarTitle className="p-l-xs">
            <BarChartIcon />
            Rating details
          </SidebarTitle>
        </SidebarMenuItem>
        <SidebarMenuItem onClick={handleChatClick}>
          <SidebarTitle className="p-l-xs">
            <MessengerIcon />
            General chat
          </SidebarTitle>
        </SidebarMenuItem>
        <SidebarMenuItem
          href={`${routes.providerPath(partnerId)}/availability_calendar`}
          selected={window.location.pathname.indexOf('availability_calendar') >= 0}
        >
          <SidebarTitle className="p-l-xs">
            <CalendarIcon />
            Availability calendar
          </SidebarTitle>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarInner>
  );
};

export const Sidebar = observer(SidebarFC);
